import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Loader2, Rabbit } from "lucide-react";
import Logo from "@/components/Logo";
import DemoPanel from "@/components/DemoPanel";

export default function LoginPage() {
  const nav = useNavigate();
  const { login, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Hoş geldiniz!");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onGuest = async () => {
    setGuestLoading(true);
    try {
      await api.post("/auth/guest");
      if (typeof refresh === "function") await refresh();
      toast.success("Misafir olarak giriş yaptınız 👋");
      nav("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setGuestLoading(false);
    }
  };

  const onGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/oauth-callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background noise-bg">
      <div className="max-w-md mx-auto px-6 pt-8 pb-16">
        <Link to="/karsilama" className="flex justify-center mb-6">
          <Logo size={80} testId="login-logo" />
        </Link>

        <h1 className="font-display text-3xl font-extrabold tracking-tight mb-1">Tekrar hoş geldiniz</h1>
        <p className="text-muted-foreground mb-8">Masaya oturmaya hazır mısınız?</p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="email">E-posta</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 h-12 rounded-xl"
              data-testid="login-email"
              placeholder="siz@example.com"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Şifre</Label>
              <Link to="/sifremi-unuttum" className="text-xs text-primary font-semibold" data-testid="login-forgot">
                Şifremi unuttum
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 h-12 rounded-xl"
              data-testid="login-password"
              placeholder="En az 6 karakter"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full h-12 rounded-full font-semibold active:scale-[0.98] transition-transform"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Giriş Yap"}
          </Button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">veya</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <Button
          variant="outline"
          onClick={onGoogle}
          data-testid="login-google"
          className="w-full h-12 rounded-full font-semibold active:scale-[0.98] transition-transform"
        >
          <GoogleIcon /> Google ile Giriş Yap
        </Button>

        <Button
          variant="ghost"
          onClick={onGuest}
          disabled={guestLoading}
          data-testid="login-guest"
          className="w-full h-11 rounded-full font-semibold mt-2 active:scale-[0.98] transition-transform text-muted-foreground hover:text-foreground"
        >
          {guestLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <><Rabbit className="h-4 w-4 mr-1.5" /> Kayıt Olmadan Misafir Olarak Dene</>
          )}
        </Button>

        <p className="text-center text-sm text-muted-foreground mt-8">
          Hesabınız yok mu?{" "}
          <Link to="/kayit" className="text-primary font-semibold" data-testid="login-go-register">
            Kayıt olun
          </Link>
        </p>

        <div className="mt-6">
          <DemoPanel />
        </div>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.5-.2-2.3H12v4.4h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.7z"/>
      <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.4 1.2-4.1 1.2-3.2 0-5.8-2.1-6.8-5H1.2v3.1A12 12 0 0 0 12 24z"/>
      <path fill="#FBBC05" d="M5.2 14.3a7.2 7.2 0 0 1 0-4.6V6.6H1.2a12 12 0 0 0 0 10.8l4-3.1z"/>
      <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.5-3.5A12 12 0 0 0 12 0 12 12 0 0 0 1.2 6.6l4 3.1c1-2.9 3.6-5 6.8-5z"/>
    </svg>
  );
}
